import { useTranslation } from "react-i18next"
import { Step } from "react-joyride"
import { CustomBodyMultiText, CustomBodyText, CustomHeader } from "../manualComponets"
import { ScrollRule } from "../UserManual"

export const operationsScrollRules: {
	target: string
	opts: ScrollRule
}[] = [
	{
		target: "#header-user-manual",
		opts: {
			containerSelector: "#body-layout",
			x: {
				offset: -24,
				align: "end",
			},
			y: {
				offset: 10000,
				align: "end",
			},
		},
	},
	{
		target: "#time-selection",
		opts: {
			containerSelector: "#body-layout",
			x: {
				offset: -24,
				align: "end",
			},
			y: {
				offset: 10000,
				align: "end",
			},
		},
	},
	{
		target: "#fuel-baseline-deviation-card",
		opts: {
			containerSelector: "#body-layout",
			x: {
				offset: 24,
				align: "start",
			},
			y: {
				offset: 100,
				align: "end",
			},
		},
	},
	{
		target: "#table-container",
		opts: {
			containerSelector: "#body-layout",
			x: {
				offset: 24,
				align: "start",
			},
			y: {
				offset: 100,
				align: "start",
			},
		},
	},
]

export const operationsSteps = (
	translationFunc?: ReturnType<typeof useTranslation>["t"]
): Step[] => {
	const t = translationFunc ?? ((text: string) => text)
	return [
		{
			target: "#header-user-manual",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("operationsTimeline.pageHeader.header")}</CustomHeader>
					<CustomBodyText>{t("operationsTimeline.pageHeader.content")}</CustomBodyText>
				</div>
			),
			placement: "left",
			disableBeacon: true,
		},
		{
			target: "#opModeSelect",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("operationsTimeline.opModeSelect.header")}</CustomHeader>
					<CustomBodyText>{t("operationsTimeline.opModeSelect.content")}</CustomBodyText>
				</div>
			),
			placement: "bottom",
			disableScrolling: true,
		},
		{
			target: "#time-selection",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("operationsTimeline.timeSelection.header")}</CustomHeader>
					<CustomBodyText>{t("operationsTimeline.timeSelection.content")}</CustomBodyText>
				</div>
			),
			placement: "bottom-end",
			disableScrolling: true,
		},
		{
			target: "#fuel-baseline-deviation-card",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>
						{t("operationsTimeline.fuelBaselineDeviationCard.header")}
					</CustomHeader>
					<CustomBodyText>
						{t("operationsTimeline.fuelBaselineDeviationCard.content")}
					</CustomBodyText>
				</div>
			),
			placement: "top",
		},
		{
			target: "#table-container",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("operationsTimeline.tableContainer.header")}</CustomHeader>
					<CustomBodyText>
						{t("operationsTimeline.tableContainer.content")}
					</CustomBodyText>
				</div>
			),
			placement: "top",
		},
		{
			target: "#table-header-date",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("operationsTimeline.tableHeaderDate.header")}</CustomHeader>
					<CustomBodyText>
						{t("operationsTimeline.tableHeaderDate.content")}
					</CustomBodyText>
				</div>
			),
			placement: "bottom",
		},
		{
			target: "#table-header-dateEnd",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("operationsTimeline.tableHeaderDateEnd.header")}</CustomHeader>
					<CustomBodyText>
						{t("operationsTimeline.tableHeaderDateEnd.content")}
					</CustomBodyText>
				</div>
			),
			placement: "bottom",
		},
		{
			target: "#table-header-opMode",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("operationsTimeline.tableHeaderOpMode.header")}</CustomHeader>
					<CustomBodyText>
						{t("operationsTimeline.tableHeaderOpMode.content")}
					</CustomBodyText>
				</div>
			),
			placement: "bottom",
		},
		{
			target: "#table-header-duration",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>
						{t("operationsTimeline.tableHeaderDuration.header")}
					</CustomHeader>
					<CustomBodyText>
						{t("operationsTimeline.tableHeaderDuration.content")}
					</CustomBodyText>
				</div>
			),
			placement: "bottom",
		},
		{
			target: "#table-header-totalFuel",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>
						{t("operationsTimeline.tableHeaderTotalFuel.header")}
					</CustomHeader>
					<CustomBodyText>
						{t("operationsTimeline.tableHeaderTotalFuel.content")}
					</CustomBodyText>
				</div>
			),
			placement: "bottom",
		},
		{
			target: "#table-header-deviation",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>
						{t("operationsTimeline.tableHeaderDeviation.header")}
					</CustomHeader>
					<CustomBodyText>
						{t("operationsTimeline.tableHeaderDeviation.content")}
					</CustomBodyText>
				</div>
			),
			placement: "bottom",
		},
		{
			target: "#expander-cell",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("operationsTimeline.expanderCell.header")}</CustomHeader>
					<CustomBodyText>{t("operationsTimeline.expanderCell.content")}</CustomBodyText>
				</div>
			),
			placement: "top-start",
		},
	]
}

export const operationalEventScrollRules: {
	target: string
	opts: ScrollRule
}[] = [
	{
		target: "#header-operational-event",
		opts: {
			containerSelector: "#body-layout",
			x: {
				offset: -24,
				align: "end",
			},
			y: {
				offset: 120,
				align: "start",
			},
		},
	},
	{
		target: "#operational-event-performance-plot",
		opts: {
			containerSelector: "#body-layout",
			x: {
				offset: 24,
				align: "start",
			},
			y: {
				offset: 120,
				align: "start",
			},
		},
	},
	{
		target: "#operational-event-generators-plot",
		opts: {
			containerSelector: "#body-layout",
			x: {
				offset: 24,
				align: "start",
			},
			y: {
				offset: 120,
				align: "start",
			},
		},
	},
	{
		target: "#operational-event-dynamic-baseline-plot",
		opts: {
			containerSelector: "#body-layout",
			x: {
				offset: 24,
				align: "start",
			},
			y: {
				offset: 120,
				align: "start",
			},
		},
	},
	{
		target: "#operational-event-map",
		opts: {
			containerSelector: "#body-layout",
			x: {
				offset: -24,
				align: "end",
			},
			y: {
				offset: 120,
				align: "start",
			},
		},
	},
	{
		target: "#operational-event-environmental-factors-plot",
		opts: {
			containerSelector: "#body-layout",
			x: {
				offset: -24,
				align: "end",
			},
			y: {
				offset: 120,
				align: "start",
			},
		},
	},
	{
		target: "#operational-event-power-consumption-plot",
		opts: {
			containerSelector: "#body-layout",
			x: {
				offset: -24,
				align: "end",
			},
			y: {
				offset: 120,
				align: "start",
			},
		},
	},
	{
		target: "#operational-event-chart-customization",
		opts: {
			containerSelector: "#body-layout",
			x: {
				offset: 24,
				align: "start",
			},
			y: {
				offset: 500,
				align: "start",
			},
		},
	},
]

export const operationalEventSteps = (
	translationFunc?: ReturnType<typeof useTranslation>["t"]
): Step[] => {
	const t = translationFunc ?? ((text: string) => text)
	return [
		{
			target: "#header-operational-event",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("operationalEvent.header.header")}</CustomHeader>
					<CustomBodyText>{t("operationalEvent.header.content")}</CustomBodyText>
				</div>
			),
			placement: "left-start",
			disableBeacon: true,
		},
		{
			target: "#operational-event-performance-plot",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("operationalEvent.performancePlot.header")}</CustomHeader>
					<CustomBodyMultiText>
						<p className={`text-dipai-text-primary px-2 text-justify`}>
							{" "}
							{t("operationalEvent.performancePlot.content")}
						</p>
						<p className={`text-dipai-text-primary px-2 text-justify`}>
							{" "}
							<span className="font-bold">
								{t("operationalEvent.performancePlot.content2.title")}
							</span>
							{t("operationalEvent.performancePlot.content2.description")}
						</p>
						<p className={`text-dipai-text-primary px-2 text-justify`}>
							{" "}
							<span className="font-bold">
								{t("operationalEvent.performancePlot.content3.title")}
							</span>
							{t("operationalEvent.performancePlot.content3.description")}
						</p>
					</CustomBodyMultiText>
				</div>
			),
			placement: "bottom",
		},
		{
			target: "#operational-event-generators-plot",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("operationalEvent.generatorsPlot.header")}</CustomHeader>
					<CustomBodyText>{t("operationalEvent.generatorsPlot.content")}</CustomBodyText>
				</div>
			),
			placement: "bottom",
		},
		{
			target: "#operational-event-dynamic-baseline-plot",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("operationalEvent.dynamicBaselinePlot.header")}</CustomHeader>
					<CustomBodyText>
						{t("operationalEvent.dynamicBaselinePlot.content")}
					</CustomBodyText>
				</div>
			),
			placement: "bottom",
		},
		{
			target: "#operational-event-power-consumption-plot",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("operationalEvent.powerConsumptionPlot.header")}</CustomHeader>
					<CustomBodyText>
						{t("operationalEvent.powerConsumptionPlot.content")}
					</CustomBodyText>
				</div>
			),
			placement: "bottom",
		},
		{
			target: "#operational-event-map",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("operationalEvent.map.header")}</CustomHeader>
					<CustomBodyText>{t("operationalEvent.map.content")}</CustomBodyText>
				</div>
			),
			placement: "bottom",
		},
		{
			target: "#operational-event-environmental-factors-plot",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>
						{t("operationalEvent.environmentalFactorsPlot.header")}
					</CustomHeader>
					<CustomBodyText>
						{t("operationalEvent.environmentalFactorsPlot.content")}
					</CustomBodyText>
				</div>
			),
			placement: "bottom",
		},
		{
			target: "#operational-event-chart-customization",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("operationalEvent.chartCustomization.header")}</CustomHeader>
					<CustomBodyText>
						{t("operationalEvent.chartCustomization.content")}
					</CustomBodyText>
				</div>
			),
			placement: "top",
		},
	]
}
